import React, { useLayoutEffect, useState } from 'react';
import {
  RiArrowRightSLine,
  RiCheckboxMultipleBlankLine,
  RiLinkM,
} from 'react-icons/ri';

import {
  Button,
  Divider,
  EnableToggle,
  HelperText,
  TextInput,
} from '../../../../../components/UiComponents/Ui';

import {
  getSupplierPostbackDetails,
  updateSupplierPostbacks,
} from '../../../../../Actions/supplierActions';
import RedirectTabSkeleton from '../redirects/RedirectTabSkeleton';
import { useQuery } from '@tanstack/react-query';
import { postbackSchema } from './postbackValidators';
import ErrorEmptyStateView from '../../../../../components/ErrorEmptyStateView';
import { getError, handleChange } from '../../../../helperFunctions';
import MultiSelectDropdown from '../../../../../components/MultiSelectDropdown';
import { useGlobalContext } from '../../../../../Contexts/GlobalContext';
import SubmitButton from '../../../../../components/UiComponents/SubmitButton';
import { formateZodError } from '../../../../../Utlils';

const postbackLinks = [
  { label: 'Complete', value: 'border-l-success-600', name: 'complete' },
  { label: 'Terminate', value: 'border-l-warning-500', name: 'terminate' },
  {
    label: 'Security Terminate',
    value: 'border-l-error-600',
    name: 'securityTerminate',
  },
  { label: 'Quota full', value: 'border-l-primary-600', name: 'quotaFull' },
];

const callbackMethodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(
  (m) => ({
    label: m,
    value: m,
  }),
);

const PostbacksAndRouting = ({ supplierID }) => {
  const { setSnackbar } = useGlobalContext();
  const [postbackData, setPostbackData] = useState({});

  const [formErrors, setFormErrors] = useState({});

  const {
    data: postbackDetails,
    refetch: postbackDetailsMutate,
    isLoading: postbackDetailsLoading,
    error: postbackDetailsError,
  } = useQuery({
    queryKey: ['supplier-postback-details', supplierID],
    queryFn: () => getSupplierPostbackDetails({ supplierID }),
    enabled: !!supplierID,
  });

  const resetToSaved = () => {
    setFormErrors({});
    setPostbackData(postbackDetails || {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const validationResult = postbackSchema.safeParse({
        ...postbackData,
        _id: supplierID,
      });

      if (validationResult.success) {
        const response = await updateSupplierPostbacks({
          ...validationResult.data,
          _id: supplierID,
        });
        if (!response?.error) {
          setSnackbar({
            open: true,
            msg: 'Postback settings updated successfully!',
            severity: 'success',
          });
          postbackDetailsMutate();
        } else {
          setSnackbar({
            open: true,
            msg: 'Internal Server Error! Please try again!',
            severity: 'error',
          });
        }
      } else {
        setFormErrors(formateZodError(validationResult.error.errors));
      }
    } catch (error) {
      console.log('error in handleSubmit from edit Supplier Postbacks', error);
      setSnackbar({
        open: true,
        msg: 'Internal Server Error! Please try again!',
        severity: 'error',
      });
      resetToSaved();
    }
  };

  useLayoutEffect(() => {
    if (postbackDetails) setPostbackData(postbackDetails);
  }, [postbackDetails]);

  const copyUrl = (url, label) => {
    if (typeof window === 'undefined' || !url) return;
    navigator.clipboard.writeText(url);
    setSnackbar({
      open: true,
      msg: `${label} Postback URL Copied!`,
      severity: 'success',
    });
  };

  if (postbackDetailsLoading) return <RedirectTabSkeleton />;

  if (postbackDetailsError) {
    return (
      <ErrorEmptyStateView
        title="Something went wrong!"
        description="Something went wrong!"
        type="error"
      />
    );
  }

  const methodError = getError(['serverCallbackMethod'], formErrors);

  return (
    <div className=" space-y-5">
      <form onSubmit={handleSubmit} className="space-y-[18px]">
        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-black">
              Postbacks & Routing
            </h2>
            <p className="text-sm font-[400] text-gray-600">
              Configure your settings from here.
            </p>
          </div>

          <Divider />

          {/* server postback toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h2 className="text-md font-bold">
                Enable <span className="text-primary-600">Server Postback</span>
              </h2>
              <p className="text-xs font-regular text-gray-500">
                Notify your server directly via a callback whenever a
                respondent&apos;s status changes.
              </p>
            </div>
            <EnableToggle
              id="server-postback"
              checked={postbackData?.serverCallback}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setPostbackData((prev) => ({
                  ...prev,
                  serverCallback: isChecked,
                  ...(!isChecked && { routing: false }),
                }));
                if (!isChecked && formErrors) {
                  const {
                    serverCallbackMethod,
                    serverCallbackEndpoints,
                    routing,
                    ...rest
                  } = formErrors;
                  setFormErrors(rest);
                }
              }}
            />
          </div>

          {/* postback redirects */}
          {postbackData?.serverCallback && (
            <div className="space-y-3 rounded-lg bg-gray-25 px-[18px] py-3 shadow-cardShadow">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h2 className="text-md font-semibold text-black">
                    Postback Redirects
                  </h2>
                  <p className="text-xs font-regular text-gray-500">
                    Configure the endpoint URL to be hit for each respondent
                    status.
                  </p>
                </div>
                <div className="w-[200px] shrink-0 space-y-1">
                  <MultiSelectDropdown
                    options={callbackMethodOptions}
                    width="200px"
                    dropdownText="Select Callback Method..."
                    selected={[
                      {
                        label: postbackData?.serverCallbackMethod || '',
                        value: postbackData?.serverCallbackMethod || '',
                      },
                    ]}
                    setSelected={(e) => {
                      const selectedValue = e;
                      handleChange(
                        ['serverCallbackMethod'],
                        selectedValue?.[0]?.value,
                        setPostbackData,
                        setFormErrors,
                        formErrors,
                      );
                    }}
                  />
                  {!!methodError && <HelperText>{methodError}</HelperText>}
                </div>
              </div>

              <Divider />

              <div className="space-y-3">
                {postbackLinks.map((d) => {
                  const url =
                    postbackData?.serverCallbackEndpoints?.[d.name] || '';
                  const err = getError(
                    ['serverCallbackEndpoints', d.name],
                    formErrors,
                  );
                  return (
                    <div
                      className="flex items-start justify-between"
                      key={d.name}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-[40px] border-l-[1.5px] ${d.value}`}
                        />
                        <h2 className="text-sm font-medium text-gray-800">
                          {d.label}
                        </h2>
                      </div>
                      <div className="w-[60%] space-y-1">
                        <TextInput
                          type="url"
                          placeholder="https://xyz.com/postback?qid=[%rid%]&status=1"
                          required={postbackData?.serverCallback}
                          startIcon={<RiLinkM size={16} />}
                          endIcon={
                            <button
                              type="button"
                              aria-label={`Copy ${d.label} URL`}
                              className="text-gray-500 hover:text-gray-700"
                              onClick={() => copyUrl(url, d.label)}
                            >
                              <RiCheckboxMultipleBlankLine size={18} />
                            </button>
                          }
                          value={url}
                          error={err}
                          onChange={(e) =>
                            handleChange(
                              ['serverCallbackEndpoints', d.name],
                              e.target.value,
                              setPostbackData,
                              setFormErrors,
                              formErrors,
                            )
                          }
                        />
                        {!!err && <HelperText>{err}</HelperText>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Divider />

          {/* routing — only once server postback is on */}
          {postbackData?.serverCallback && (
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h2 className="text-md font-bold">
                  Enable <span className="text-purple-600">Routing</span>
                </h2>
                <p className="text-xs font-regular text-gray-500">
                  Route respondents through the configured postback endpoints as
                  part of the survey flow before completion.
                </p>
              </div>
              <EnableToggle
                id="routing"
                checked={postbackData?.routing}
                onChange={(e) =>
                  handleChange(
                    ['routing'],
                    e.target.checked,
                    setPostbackData,
                    setFormErrors,
                    formErrors,
                  )
                }
              />
            </div>
          )}
        </div>

        <section className="sticky bottom-0 flex h-[10%] w-full items-center justify-end gap-3 border-t border-gray-200 bg-white py-3">
          <div className="flex justify-end gap-2">
            <Button
              variant="outlined"
              intent="default"
              type="button"
              className="w-[100px]"
              onClick={resetToSaved}
            >
              Cancel
            </Button>
            <SubmitButton
              name="Save Settings"
              endIcon={<RiArrowRightSLine size={20} />}
            />
          </div>
        </section>
      </form>
    </div>
  );
};

export default PostbacksAndRouting;
